import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BottomNav from "../components/BottomNav";
import InternalHeader from "../components/InternalHeader";
import { useAppState } from "../context/AppState";
import { authenticatedApi } from "../lib/api";

const steps = [
  { statuses: [1, 8, 10], label: "Pedido enviado", detail: "El negocio recibió tu solicitud." },
  { statuses: [2], label: "Pedido aceptado", detail: "El negocio está preparando tu pedido." },
  { statuses: [7], label: "Pedido listo", detail: "El pedido ya está listo para ser recogido." },
  { statuses: [3], label: "Motorizado asignado", detail: "Un repartidor fue asignado a tu pedido." },
  { statuses: [4], label: "En camino", detail: "Tu pedido está viajando hacia tu dirección." },
  { statuses: [5], label: "Entregado", detail: "El pedido fue entregado." },
];

function money(value) {
  return `S/ ${Number(value || 0).toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("es-PE", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

export default function OrderDetailPage() {
  const { uniqueOrderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAppState();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    if (!user?.auth_token) return;
    setLoading(true);
    setError("");
    try {
      const response = await authenticatedApi("/order-flow-v1", user.auth_token, {
        method: "POST",
        body: JSON.stringify({ unique_order_id: uniqueOrderId }),
      });
      const [orderItems, orderHistory] = await Promise.all([
        authenticatedApi("/get-order-items", user.auth_token, {
          method: "POST",
          body: JSON.stringify({ order_id: response.order.id }),
        }),
        authenticatedApi("/get-orders", user.auth_token, {
          method: "POST",
          body: "{}",
        }),
      ]);
      const historyOrder = orderHistory?.data?.find(
        (entry) => Number(entry.id) === Number(response.order.id),
      );
      setOrder({ ...response.order, ...historyOrder, flow_v1: response.order.flow_v1 });
      setItems(Array.isArray(orderItems) ? orderItems : []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [uniqueOrderId, user?.auth_token]);

  useEffect(() => {
    if (!order || ![1, 2, 3, 4, 7, 8, 10].includes(Number(order.orderstatus_id))) return;
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, [order?.id, order?.orderstatus_id, user?.auth_token]);

  const visibleSteps = useMemo(() => {
    if (Number(order?.orderstatus_id) === 6) return [];
    return steps.filter((step) => {
      if (step.statuses.includes(7)) return Number(order?.orderstatus_id) === 7;
      return true;
    });
  }, [order?.orderstatus_id]);
  const activeRank = Math.max(
    0,
    visibleSteps.findIndex((step) => step.statuses.includes(Number(order?.orderstatus_id))),
  );

  const cancelOrder = async () => {
    if (!reason.trim()) {
      setError("Escribe brevemente por qué deseas cancelar.");
      return;
    }
    setCancelling(true);
    setError("");
    try {
      const response = await authenticatedApi("/cancel-order", user.auth_token, {
        method: "POST",
        body: JSON.stringify({ order_id: order.id, cancellation_reason: reason.trim() }),
      });
      if (!response.success) throw new Error("El plazo de cancelación ya terminó.");
      setCancelOpen(false);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <main className="food-app">
      <InternalHeader title="Mis pedidos" backTo="/orders" />
      <div className="order-detail">
        {!user && (
          <section className="empty-cart">
            <span>🔐</span>
            <h2>Inicia sesión para ver este pedido</h2>
            <Link to={`/account?next=/orders/${encodeURIComponent(uniqueOrderId)}`}>Ir a mi cuenta</Link>
          </section>
        )}
        {loading && <div className="food-message"><b>Cargando seguimiento…</b></div>}
        {error && <div className="food-message food-message--error"><b>{error}</b></div>}

        {order && (
          <>
            <header className="order-detail__heading">
              <div>
                <small>PEDIDO {order.unique_order_id}</small>
                <h1>{order.restaurant?.name || "Delivery Huaraz"}</h1>
                <time>{formatDate(order.created_at)}</time>
              </div>
              <strong>{money(order.total)}</strong>
            </header>

            {Number(order.orderstatus_id) === 6 ? (
              <section className="order-cancelled">
                <span>×</span>
                <div>
                  <h2>Pedido cancelado</h2>
                  <p>{order.cancellation_reason || "Este pedido fue cancelado."}</p>
                </div>
              </section>
            ) : (
              <section className="order-timeline">
                {visibleSteps.map((step, index) => {
                  const done = index <= activeRank;
                  const current = index === activeRank;
                  return (
                    <article className={`${done ? "is-done" : ""} ${current ? "is-current" : ""}`} key={step.label}>
                      <span>{done ? "✓" : index + 1}</span>
                      <div><b>{step.label}</b><small>{step.detail}</small></div>
                    </article>
                  );
                })}
              </section>
            )}

            {order.flow_v1?.preparation_minutes > 0 && ![5, 6].includes(Number(order.orderstatus_id)) && (
              <section className="order-flow-note">
                <span>⏱️</span>
                <div>
                  <b>Preparación estimada: {order.flow_v1.preparation_minutes} minutos</b>
                  <small>
                    {order.flow_v1.is_late
                      ? "El tiempo estimado ya pasó; el negocio está terminando tu pedido."
                      : "El tiempo se actualiza con la información del negocio."}
                  </small>
                </div>
              </section>
            )}

            {order.flow_v1?.driver_customer_note && (
              <section className="order-flow-note">
                <span>🏍️</span>
                <div><b>Mensaje del motorizado</b><small>{order.flow_v1.driver_customer_note}</small></div>
              </section>
            )}

            {order.flow_v1?.waiting_store_acceptance && order.delivery_contact && (
              <section className="order-flow-note">
                <span>🏍️</span>
                <div>
                  <b>{order.delivery_contact.name} reservó tu pedido</b>
                  <small>El negocio todavía debe aceptarlo. Para cancelar, comunícate primero con el motorizado.</small>
                  <p><a href={`tel:${order.delivery_contact.phone}`}>Llamar</a> · <a href={`https://wa.me/${String(order.delivery_contact.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer">WhatsApp</a></p>
                </div>
              </section>
            )}

            <section className="order-detail__card">
              <h2>Detalle del pedido</h2>
              {items.map((item) => (
                <div className="order-item-row" key={item.id}>
                  <span>{item.quantity} × {item.name}</span>
                  <b>{money(Number(item.price) * Number(item.quantity))}</b>
                </div>
              ))}
              <div className="order-item-row is-total"><span>Total</span><b>{money(order.total)}</b></div>
            </section>

            <section className="order-detail__card">
              <h2>Entrega y pago</h2>
              <p><b>Dirección:</b> {order.delivery_address || "No especificada"}</p>
              {order.landmark && <p><b>Referencia:</b> {order.landmark}</p>}
              <p><b>Forma de pago:</b> {order.payment_display || order.payment_mode || "No especificado"}</p>
              {order.cash_payment_method === "CASH" && Number(order.cash_change_amount) > 0 && (
                <p><b>Preparar vuelto de:</b> {money(order.cash_change_amount)}</p>
              )}
            </section>

            <div className="order-detail__actions">
              {order.is_ratable && <Link to={`/rate-order/${order.id}`}>Calificar pedido</Link>}
              {order.flow_v1?.customer_can_cancel && (
                <button type="button" onClick={() => setCancelOpen(true)}>Cancelar pedido</button>
              )}
            </div>

            {cancelOpen && (
              <section className="cancel-order-box">
                <h2>¿Deseas cancelar este pedido?</h2>
                <p>Solo se permite durante el plazo indicado por el negocio.</p>
                <textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Motivo de cancelación"
                  maxLength={500}
                />
                <div>
                  <button type="button" onClick={() => setCancelOpen(false)}>Volver</button>
                  <button type="button" disabled={cancelling} onClick={cancelOrder}>
                    {cancelling ? "Cancelando…" : "Confirmar cancelación"}
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </div>
      <BottomNav />
    </main>
  );
}
